import axios from 'axios';
import actions from '../constants/actions'
import { isObject, parseUrlParams } from '../helpers/common'
import {postMessageSuccess, postMessageError} from '../helpers/postMessage'
import {clearPreconsentData} from '../helpers/consent'

const requestExtender = (config) => {
    //Adding suffix for backend debugging
    const envSuffix = process.env.REACT_APP_API_URL_SUFFIX;
    if (envSuffix === undefined) {
        return config;
    }
    
    let newConfig = Object.assign({}, config);
    const suffix = newConfig.url.includes('?') ? '&' + (envSuffix).replace('?','') : envSuffix
    newConfig.url = newConfig.url + suffix
    return newConfig;
}

const requestWithoutSession = axios.create({
    baseURL: process.env.REACT_APP_API_ENDPOINT_BASE,
    timeout: process.env.REACT_APP_REQUEST_WITHOUT_SESSION_TIMEOUT,
    validateStatus: function (status) {
        return status <= 500; // Reject only if the status code is greater than 500
    }
}); //For requests to backend WITHOUT session key
requestWithoutSession.interceptors.request.use(requestExtender);

const requestWithSession = axios.create({
    baseURL: process.env.REACT_APP_API_ENDPOINT_BASE,
    timeout: process.env.REACT_APP_REQUEST_WITH_SESSION_TIMEOUT,
    validateStatus: function (status) {
        return status <= 500; // Reject only if the status code is greater than 500
    }
}); //For requests to backend WITH session key. Session key will be populated during startSession() call
requestWithSession.interceptors.request.use(requestExtender);

export const closeBrowserSession = (consentGuid, userToken) => {
    if (!consentGuid) return Promise.resolve();

    return requestWithSession.post('/close-session', {
        consent_guid: consentGuid,
        ...(userToken && {user_token: userToken})
    }, {
        adapter: 'fetch',
        fetchOptions: {keepalive: true}
    });
}

//Reports to backend that window.open() was blocked by the browser's popup blocker
export const logPopupBlocked = (consentGuid, slug) => async (dispatch, getState) => {
    const state = getState();
    const userToken = state.currentLogin?.user_token || state.sessionData?.user_token;

    dispatch(postMessageError("popup_blocked", 0, 'Popup blocked by browser', slug));

    try {
        await requestWithSession.post('/popup-blocked', {
            consent_guid: consentGuid,
            slug: slug,
            user_agent: navigator.userAgent,
            ...(userToken && {user_token: userToken})
        });
    } catch (error) {}
}


//Common wrapper for any CRITICAL_ERROR that came from portal API / network malfunction
const criticalApiError = (processName, payload) => async (dispatch) => {

    dispatch({type: actions.CRITICAL_ERROR, message: 'Please contact customer support. '}); //Common action to set up critical error screen
}

//Common wrapper for any error during institution login process. This is not whole session critical error
const institutionApiError = (processName, payload,institution_slug = null) => async (dispatch) => {
    let additionalInfo;
    if (typeof payload == 'string') {
        additionalInfo = checkMessage(payload);
    } else {
        if (payload && payload.response && payload.response.error != null) {
            let error = payload.response.error;
            additionalInfo = checkMessage(error,institution_slug);
        } else {
            additionalInfo = 'Please contact customer support.';
        }
    }
    dispatch({type: actions.INSTITUTION_ERROR, message: additionalInfo}); //Common action to set up current institution's error screen
}

function checkMessage(payload,institution_slug = null) {
    let additionalInfo = 'Please contact customer support.';
    if(payload.includes('20008')){
        if (payload.includes('Incorrect Date of Birth Format')){// maintaining the same error code
            additionalInfo = 'Please confirm your credentials. Error code (20008)';
        } else if (institution_slug === 'suncorp') {
            additionalInfo = 'Please confirm username/password. If you have a security token or use the Suncorp Secured App you must enter a token code to login. Error code (20008)'
        } else {
            additionalInfo = 'Please confirm username/password. Error code (20008)';
        }        
    }else if (payload.includes('20005')){
        additionalInfo = 'Your online banking account has been locked. You will need to check with your bank for resetting your access. Error code (20005)';
    }else if (payload.includes('20007')){
        additionalInfo = 'Your online banking password has expired. You will need to check with your bank for instructions on resetting your password. Error code (20007)';
    }else if (payload.includes('32008')){
        additionalInfo = 'Your online banking setup is not yet finalised. You will need to check with your bank for instructions on completing setup. Error code (32008)';
    }else if (payload.includes('30004')){
        additionalInfo = 'Your financial institution requires you to perform some manual action before we can export data from your account. Please log in to your financial institution\'s online banking (desktop site) and complete the action requested after you log in. Error code (30004)';
    }else if (payload.includes('10104')){
        additionalInfo = 'The login credentials are not valid. Error code (10104)';
    }else if (payload.includes('10105')){
        additionalInfo = 'Unable to log in to bank account. Credentials may be incorrect. Error code (10105)';
    }else if (payload.includes('20001')){
        additionalInfo = 'Too many login attempts. Error code (20001)';
    }else if (payload.includes('20002')){
        additionalInfo = 'Required credential missing. Error code (20002)';
    }else if (payload.includes('20004')){
        additionalInfo = 'Already logged in. Error code (20004)';
    }else if (payload.includes('30026')){
        additionalInfo = 'This institution is currently experiencing high number of requests being processed,and its unable to process any additional request at this time. We would suggest you try again after 5 minutes.';
    }else if (payload.includes('30027')){
        additionalInfo = 'We tried reprocessing your request after certain time however we were not successful. You could try again after 24 hours.';
    }else if (payload.includes('19002') && payload.includes('Consent authorization timed out')){
        additionalInfo = 'Your session has expired due to inactivity. Please refresh or try again.';
    }
    return additionalInfo;
}

const initTabMonitor = (dispatch) => {
    const bc = new BroadcastChannel("submission-single-tab");
    bc.onmessage = (event) => {
        if (event.data === `Am I the first tab?`) {
            bc.postMessage(`No you're not.`);
            //alert(`Another tab of this site just got opened`);
        }
        if (event.data === `No you're not.`) {
            alert(`An instance of this submission is already running. You need to close this session.`);
            dispatch({type: actions.CRITICAL_ERROR, message: 'You cannot make submissions while there is a submission in progress in another tab.'});
        }
    };
    bc.postMessage(`Am I the first tab?`);
}

export function sanitizeString(input = '', format = false) {
    if (!input.trim()) return '';

    // Remove unwanted characters and normalize whitespace
    let sanitized = input.trim().replace(/[^a-zA-Z\s-'.]/g, '');
    sanitized = sanitized.replace(/\s+/g, ' ');

    return format ? sanitized.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : sanitized;
}

export function sanitizeEmail(email = '') {
    if (!email.trim()) return '';

    const sanitized = email.trim().toLowerCase().replace(/[^a-z0-9@._-]/g, '');
    return sanitized;
}

export function isValidNameFormat(name) {
    return /^[a-zA-Z\s]+$/.test(name.trim());
}

export function isValidLength(name, length) {
    return name.trim().length <= length;
}

export function isValidEmailFormat(email) {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.trim());
}

export function storeQueryParamsToLocalStorage() {
    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.has('name') || queryParams.has('surname') || queryParams.has('email') || queryParams.has('phone')) {
        localStorage.setItem('preconsent_name', queryParams.get('name')?.trim() || '');
        localStorage.setItem('preconsent_surname', queryParams.get('surname')?.trim() || '');
        localStorage.setItem('preconsent_email', queryParams.get('email')?.trim() || '');
        localStorage.setItem('preconsent_phone', queryParams.get('phone')?.trim() || '');
        localStorage.setItem('preconsent_loaded_once', 'true');

        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        window.location.reload();

        return true; // Indicates reload triggered
    }    
    else {
        // If previously loaded with params, and now no params — clear everything        
        if (!localStorage.getItem('preconsent_loaded_once')) {
            localStorage.removeItem('preconsent_name');
            localStorage.removeItem('preconsent_surname');
            localStorage.removeItem('preconsent_email');            
            localStorage.removeItem('preconsent_phone');
        }
        localStorage.removeItem('preconsent_loaded_once');
        return false;
    }
}

export function getPreconsentDataFromStorage() {
    return {
        rawGivenName: localStorage.getItem('preconsent_name') || '',
        rawSurname: localStorage.getItem('preconsent_surname') || '',
        rawEmail: localStorage.getItem('preconsent_email') || '',
        rawPhone: localStorage.getItem('preconsent_phone') || ''
    };
}

export function validatePreconsentData(rawGivenName, rawSurname, rawEmail) {
    const errors = [];

    const invalidFormatGiven = rawGivenName && !isValidNameFormat(rawGivenName);
    const invalidFormatSurname = rawSurname && !isValidNameFormat(rawSurname);

    if (invalidFormatGiven && invalidFormatSurname) {
        errors.push("Only alphabets and spaces are allowed in Given Name and Surname.");
    } else if (invalidFormatGiven) {
        errors.push("Only alphabets and spaces are allowed in Given Name.");
    } else if (invalidFormatSurname) {
        errors.push("Only alphabets and spaces are allowed in Surname.");
    }

    const longGiven = rawGivenName && !isValidLength(rawGivenName, 50);
    const longSurname = rawSurname && !isValidLength(rawSurname, 50);

    if (longGiven && longSurname) {
        errors.push("Maximum of 50 characters allowed in Given Name and Surname.");
    } else if (longGiven) {
        errors.push("Maximum of 50 characters allowed in Given Name.");
    } else if (longSurname) {
        errors.push("Maximum of 50 characters allowed in Surname.");
    }

    if (!rawEmail) {
        errors.push("Email is required.");
    } else if (!isValidEmailFormat(rawEmail)) {
        errors.push("Not a valid email format.");
    } else if (!isValidLength(rawEmail, 100)) {
        errors.push("Maximum of 100 characters allowed in Email.");
    }

    return errors;
}

export function cleanClientCode(clientCode) {
    const parts = clientCode.split('/');
    if (parts.length === 3 && parts[1].toLowerCase() === 'iframe') {
        return parts[2];
    }
    return clientCode.replace(new RegExp('/', 'g'), '');
}

//Function starts new session via portal, initiates theme and institution list load on success
//If client code in not found we show 404 screen
//On error we throw CRITICAL_ERROR, as whole session is basically useless
export const startSession = (clientCode, defaultWhitelabel) => async (dispatch) => {
    initTabMonitor(dispatch);

    if (storeQueryParamsToLocalStorage()) return;

    
    const { rawGivenName, rawSurname, rawEmail, rawPhone } = getPreconsentDataFromStorage();
    const errors = validatePreconsentData(rawGivenName, rawSurname, rawEmail);

    const given_name = sanitizeString(rawGivenName, true);
    const surname = sanitizeString(rawSurname, true);
    const email = sanitizeEmail(rawEmail);

    const cleanCode = cleanClientCode(clientCode);
    if (cleanCode.length === 0) {
        dispatch({ type: actions.SESSION_START_NO_CODE_PROVIDED });
        return;
    }
    
    //Start session via portal
    try {
        let { data, status } = await requestWithoutSession.post('init/' + cleanCode);

        if ('sessionToken' in data) {
            requestWithSession.defaults.headers.common['X-SESSION-TOKEN'] = data.sessionToken; //Put it in global request mechanism right away
            dispatch({type: actions.SESSION_START_SUCCESS, referralCode: clientCode, ...data});

            
            // If valid query string values exist, dispatch PRECONSENT_FINISHED
            if (given_name || surname || email || rawPhone) {
                dispatch({
                    type: actions.STORE_PRECONSENT_FORM_DATA,
                    given_name,
                    surname,
                    email,
                    phone: rawPhone,
                    errors
                });
            }


            let receivedWlName = data.whitelabel.replace(new RegExp('wl:', 'g'), '');
            let whitelabel = receivedWlName.length > 0 ? receivedWlName : defaultWhitelabel;

            //if no white label received , set it to illion
            if(whitelabel.trim() === ''){
                whitelabel = 'illion';
            }

            let wlStyles;
            let wlTheme;
            if ('whitelabelData' in data) {
                wlStyles = data.whitelabelData.styles;
                wlTheme = data.whitelabelData.theme;
                dispatch({type: actions.INJECT_THEME, styles: wlStyles, theme: wlTheme})
            } else {
                dispatch(loadTheme(whitelabel));
            }

            dispatch(getInstitutions());

        } else {
            dispatch(criticalApiError('START_SESSION', {status: status, response: data}));
        }

    } catch(error) {

        if (error.response) { //We got some answer from API
            dispatch(criticalApiError('START_SESSION', ' '));
        } else if (error.message) {
            dispatch(criticalApiError('START_SESSION', ' '));
        } else {
            dispatch(criticalApiError('START_SESSION', ' '));
        }

    }
}

//Function obtains whitelabel data (json file stored separately) and then sets SESSION as successfully started
//On error we throw CRITICAL_ERROR, as whole session is basically useless
export const loadTheme = (whitelabel) => async (dispatch) => {

    dispatch({type: actions.GET_THEME_START})

    try {

        let loc = window.location;
        let targetUrl = loc.protocol + '//' + loc.hostname + (loc.port ? ':' + loc.port: '') + '/whitelabel/' + whitelabel + '/wl.json';
        let { data, status } = await requestWithoutSession.get(targetUrl);

        switch (status) {
            case 200:
                dispatch({type: actions.SET_WL, whitelabel: whitelabel});
                setTimeout(() => {
                    dispatch({type: actions.GET_THEME_SUCCESS, theme: data})
                  }, 100)
                
                break
            default:
                dispatch(criticalApiError('LOAD_THEME', {url: targetUrl, status: status}));
        }
        
    } catch(error) {

        if (error.response) { //We got some answer from API
            dispatch(criticalApiError('LOAD_THEME', ' '));
        } else if (error.message) {
            dispatch(criticalApiError('LOAD_THEME', ' '));
        } else {
            dispatch(criticalApiError('LOAD_THEME', ' '));
        }

    }
}

//Function obtains full list of institutions from portal
//On error we throw CRITICAL_ERROR, as whole session is basically useless
export const getInstitutions = () => async (dispatch) => {

    dispatch({type: actions.GET_INSTITUTIONS_START})

    try {
        let { data, status } = await requestWithSession.get('/institutions');

        switch (status) {
            case 200:
                dispatch({type: actions.GET_INSTITUTIONS_START_SUCCESS, data: data.institutions})
                break
            default:
                dispatch(criticalApiError('GET_INSTITUTIONS', {status: status, response: data}));
        }
        
    } catch(error) {

        if (error.response) { //We got some answer from API
            dispatch(criticalApiError('GET_INSTITUTIONS', ' '));
        } else if (error.message) {
            dispatch(criticalApiError('GET_INSTITUTIONS', ' '));
        } else {
            dispatch(criticalApiError('GET_INSTITUTIONS', ' '));
        }

    }
}

//Function performs institution preload via portal (if required) and continues to normal login
//On any error (network or received from portal) we throw non-critical 
//institutionApiError that affects only current login process, not whole session
export const institutionSelected = (slug, requires_preload, has_nzob_enabled)  => async (dispatch, getState) => {

    if(has_nzob_enabled === true){
        dispatch(postMessageSuccess("bank_selected", {data: slug}));
        dispatch({type: actions.PRECONSENT_LOADER, data: {slug: slug}});

        try {
            if(getState().sessionData.preconsentFinished === true && getState().sessionData.consentCustomerId){

                let { data, status } = await requestWithSession.get('/preconsent/' + slug + '/' + getState().sessionData.consentCustomerId);

                switch (status) {
                    case 200:
                        dispatch({ type: actions.CONSENT_LOAD, data: {
                            slug: slug, 
                            consent:data.consent_data, 
                            consent_guid:data.consent_guid
                        }});
                        dispatch(postMessageSuccess("pre_consentform", {data: slug}));
                        break
                    default:
                        dispatch(criticalApiError('PRECONSENT_FORM_ERRROR', {status: status, response: data}));
                }

            }
            else {

                let { data, status } = await requestWithSession.get('/preconsent/' + slug);

                switch (status) {
                    case 200:
                        dispatch({type: actions.PRECONSENT_FORM_LOADED, data: {form: data.form, slug: slug, institution: slug}});
                        break
                    default:
                        dispatch(criticalApiError('PRECONSENT_FORM_ERRROR', {status: status, response: data}));
                }

            }
            
            
        } catch(error) {

            if (error.response) { //We got some answer from API
                dispatch(criticalApiError('PRECONSENT_FORM_ERRROR', ' '));
            } else if (error.message) {
                dispatch(criticalApiError('PRECONSENT_FORM_ERRROR', ' '));
            } else {
                dispatch(criticalApiError('PRECONSENT_FORM_ERRROR', ' '));
            }

        }
    }
    else if (requires_preload === "1") {

        dispatch({type: actions.PRELOAD_INSTITUTION_START, slug: slug});

        try {
            let { data, status } = await requestWithSession.get('/preload/' + slug);
    
            switch (status) {
                case 200:
                    dispatch({type: actions.PRELOAD_INSTITUTION_SUCCESS, data: data.institution}); //Note: Event triggers two reducers
                    dispatch(postMessageSuccess("bank_selected", {data: slug}));
                    break
                default:
                    dispatch(institutionApiError('PRELOAD', {status: status, response: data}));
                    dispatch(postMessageError("bank_selected", data.errorCode, data.error, slug));
            }
            
        } catch(error) {
            if (error.response) { //We got some answer from API
                dispatch(institutionApiError('PRELOAD', error.response.data));
            } else if (error.message) {
                dispatch(institutionApiError('PRELOAD', error.message));
            } else {
                dispatch(institutionApiError('PRELOAD', error.toString()));
            }
            dispatch(postMessageError("bank_selected", 0, 'N/A'));
        }
    } else {
        dispatch({type: actions.INSTITUTION_SELECTED, slug: slug});
        dispatch(postMessageSuccess("bank_selected", {data: slug}));
    }    
}

//Function performs login to institution via portal
//On any error (network or received from portal) we throw non-critical 
//institutionApiError that affects only current login process, not whole session
export const institutionLogin = (institution, credentials) => async (dispatch, getState) => {
    
    dispatch({type: actions.LOGIN_SUBMIT_START, credentials: credentials})
    
    try {
        const postData = {
            'credentials' : {...credentials, institution: institution.slug}, 
            'name': getState().sessionData.userFullName,
            'urlParams' : parseUrlParams(window.location.search, ['ip'])
        };
        
        let { data, status } = await requestWithSession.post('/login', postData);

        switch (status) {
            case 200:
                if (isObject(data) && data.hasOwnProperty('accountsSelectionSubmitted')) {
                    dispatch({ type: actions.LOGIN_PROCESS_FINISHED, data: {slug: institution.slug, name: institution.name}}) 
                    dispatch(postMessageSuccess("submission_complete", {data: institution.slug}));
                }else if (isObject(data) && data.hasOwnProperty('accounts')) {
                    dispatch({type: actions.LOGIN_PROCESS_ACCOUNTS_SELECTION, data: {accounts: data.accounts, slug: institution.slug, name: institution.name}});
                    dispatch(postMessageSuccess("login_clicked", {data: institution.slug}));
                } else if (isObject(data) && data.hasOwnProperty('mfa')) {
                    dispatch({type: actions.LOGIN_PROCESS_MFA, data: {slug: institution.slug, mfa : data.mfa}});
                    dispatch(postMessageSuccess("login_clicked", {data: institution.slug}));
                } else {
                    dispatch(institutionApiError('LOGIN', {reason: "UNHANDLED_DATA", response: data},institution.slug));
                    dispatch(postMessageError("login_clicked", 0, 'N/A'));
                }                
                break
            default:
                dispatch(institutionApiError('LOGIN', {status: status, response: data},institution.slug));
                dispatch(postMessageError("login_clicked", data.errorCode, data.error, institution.slug));
        }
        
    } catch(error) {
        if (error.response) { //We got some answer from API
            dispatch(institutionApiError('LOGIN', error.response.data));
        } else if (error.message) {
            dispatch(institutionApiError('LOGIN', error.message));
        } else {
            dispatch(institutionApiError('LOGIN', error.toString()));
        }
        dispatch(postMessageError("login_clicked", 0, 'N/A'));
    }
}

//Function performs MFA data sending to institution via portal
//On any error (network or received from portal) we throw non-critical 
//institutionApiError that affects only current login process, not whole session
export const processMFA = (institution, mfaFields) => async (dispatch) => {
    
    dispatch({ type: actions.MFA_SUBMIT_START})

    try {


        //add institution to support auto account selection
        if(isObject(mfaFields)){
            mfaFields.institution = institution.slug;
        }

        const postData = mfaFields;
        let { data, status } = await requestWithSession.post('/mfa', postData);
        switch (status) {
            case 200:
                if (isObject(data) && data.hasOwnProperty('accountsSelectionSubmitted')) {
                    dispatch({ type: actions.LOGIN_PROCESS_FINISHED, data: {slug: institution.slug, name: institution.name}}) 
                    dispatch(postMessageSuccess("submission_complete", {data: institution.slug}));
                }else if (isObject(data) && data.hasOwnProperty('accounts')) {
                    if(institution.slug==='winz'){
                        // automatically send get account data request.
                        let winzAccount=[0];
                        dispatch(submitSelectedAccounts (institution, winzAccount, ''));
                        return;
                    }
                    dispatch({ type: actions.LOGIN_PROCESS_ACCOUNTS_SELECTION, data: {accounts: data.accounts, slug: institution.slug, name: institution.name} })
                } else if (isObject(data) && data.hasOwnProperty('mfa')) {
                    dispatch({type: actions.LOGIN_PROCESS_MFA, data: {slug: institution.slug, mfa : data.mfa}})
                } else {
                    dispatch(institutionApiError('MFA', {reason: "UNHANDLED_DATA", response: data}));
                }
                break
            default:
                dispatch(institutionApiError('MFA', {status: status, response: data}));
        }
        
    } catch(error) {
        if (error.response) { //We got some answer from API
            dispatch(institutionApiError('MFA', error.response.data));
        } else if (error.message) {
            dispatch(institutionApiError('MFA', error.message));
        } else {
            dispatch(institutionApiError('MFA', error.toString()));
        }  
    }
}

//Function performs sending of selected accounts to institution via portal
//On any error (network or received from portal) we throw non-critical 
//institutionApiError that affects only current login process, not whole session
export const submitSelectedAccounts = (institution, selectedAccounts, password) => async (dispatch, getState) => {

    dispatch({ type: actions.ACCOUNTSELECTION_SUBMIT_START})
    dispatch(postMessageSuccess("accounts_selected", {data: institution.slug}));

    try {    
        let postData = {
            accounts: {[institution.slug]: selectedAccounts},
            password : password
        };

        if(getState().sessionData.clientSettings.forceNoAccounts) {
            postData['forceNoAccounts'] = true;
        }

        let { data, status } = await requestWithSession.post('/selectAccounts', postData);
        switch (status) {
            case 200:
                    dispatch({ type: actions.LOGIN_PROCESS_FINISHED, data: {slug: institution.slug, name: institution.name}}) //Clear the "current login process" view
                    dispatch(postMessageSuccess("submission_complete", {data: institution.slug}));
                break
            default:
                dispatch(institutionApiError('ACCOUNTSELECTION', {status: status, response: data}));
                dispatch(postMessageError("submission_complete", data.errorCode, data.error, institution.slug));
        }
        
    } catch(error) {
        if (error.response) { //We got some answer from API
            dispatch(institutionApiError('ACCOUNTSELECTION', error.response.data));
        } else if (error.message) {
            dispatch(institutionApiError('ACCOUNTSELECTION', error.message));
        } else {
            dispatch(institutionApiError('ACCOUNTSELECTION', error.toString()));
        }
        dispatch(postMessageError("submission_complete", 0, 'N/A'));
    }
}

//Function finalises whole session by sending signal to portal
//On error we throw CRITICAL_ERROR, as whole session is basically useless
export const submitAll = () => async (dispatch) => {
    
    dispatch({type: actions.SUBMIT_ALL_START});

    try {

        let { data, status } = await requestWithSession.post('/finished');

        switch (status) {
            case 200:
                dispatch({type: actions.SUBMIT_ALL_SUCCESS});
                dispatch(postMessageSuccess("submit_all"));
                break
            default:
                dispatch(criticalApiError('SUBMIT_ALL', {status: status, response: data}));
                dispatch(postMessageError("submit_all", data.errorCode, data.error));
        }
        
    } catch(error) {
        if (error.response) { //We got some answer from API
            dispatch(criticalApiError('SUBMIT_ALL', ' '));
        } else if (error.message) {
            dispatch(criticalApiError('SUBMIT_ALL', '  '));
        } else {
            dispatch(criticalApiError('SUBMIT_ALL', ' '));
        }
        dispatch(postMessageError("submit_all", 0, 'N/A'));
    }    
}

export const sendLogEntry = async (data) => {
    try {
        //console.warn('MIDDLEWARE: ', data)
        //await requestWithoutSession.post('/frontendlog', data);
    } catch(error) {}
}

// Function performs PreConsent Form submissions for NZ OB
// On any error (network or received from portal) we throw non-critical
export const preConsentFormSubmissions = (slug, userdata) => async (dispatch, getState) => {
    
    dispatch({type: actions.PRECONSENT_LOADER, data: {slug: slug}});
    
    try {
        const postData = {
            'userdata' : {...userdata, institution: slug}, 
            'name': getState().sessionData.userFullName,
            'urlParams' : parseUrlParams(window.location.search, ['ip'])
        };
        
        let { data, status } = await requestWithSession.post('/preconsent', postData);

        switch (status) {            
            case 200:
                dispatch({type: actions.PRECONSENT_FINISHED, preconsentFinished: true, consentCustomerId: data.customer_id});
                // clear the preconsent data from localStorage
                clearPreconsentData();
                dispatch({ type: actions.CONSENT_LOAD, data: {
                    slug: slug, 
                    consent:data.consent_data, 
                    consent_guid:data.consent_guid
                }}) 
                dispatch(postMessageSuccess("pre_consentform", {data: slug}));                              
                break
            default:
                dispatch({type: actions.PRECONSENT_FINISHED, preconsentFinished: false});
                dispatch(institutionApiError('PRECONSENT_FORM_SUBMITTED', {status: status, response: data},slug));
        }
        
    } catch(error) {
        if (error.response) {
            dispatch(institutionApiError('PRECONSENT_FORM_SUBMITTED', error.response.data, slug));
        } else if (error.message) {
            dispatch(institutionApiError('PRECONSENT_FORM_SUBMITTED', error.message, slug));
        } else {
            dispatch(institutionApiError('PRECONSENT_FORM_SUBMITTED', error.toString(), slug));
        }
        dispatch(postMessageError("pre_consentform", 0, 'N/A'));
    }
}

export const initialiseDecoupledFlow = (slug, isDeidentified = false) => async (dispatch, getState) => {
    
    dispatch({type: actions.PRECONSENT_LOADER, data: {slug: slug}});

    try {       
        
        let { data, status } = await requestWithSession.post('/initialise_decoupled', {
            is_deidentified: isDeidentified,
        });

        switch (status) {            
            case 200:
                
                dispatch(postMessageSuccess("decoupled_flow_initialised", {data: slug}));
                dispatch({ type: actions.CONSENT_DECOUPLED_INITIALISED, data: {
                    slug: slug, 
                    decoupled_flow:data.decoupled_flow,
                    consent_guid:data.consent_guid,
                    user_token:data.user_token
                }});
                
                break
            default:
                //dispatch({ type: actions.CONSENT_ERROR, data: {slug: slug}}) 
                dispatch(institutionApiError('CONSENT_DECOUPLED_INITIALISED', {status: status, response: data}, slug));
        }
        
    } catch(error) {
        if (error.response) { //We got some answer from API
            dispatch(institutionApiError('CONSENT_DECOUPLED_INITIALISED', error.response.data, slug));
        } else if (error.message) {
            dispatch(institutionApiError('CONSENT_DECOUPLED_INITIALISED', error.message, slug));
        } else {
            dispatch(institutionApiError('CONSENT_DECOUPLED_INITIALISED', error.toString(), slug));
        }
        dispatch(postMessageError("consent_decoupled_initialised", 0, 'N/A'));
    }
}

// Function performs Initialise Consent
// On any error (network or received from portal) we throw non-critical
export const initialiseConsent = (slug, formData = null) => async (dispatch, getState) => {
    
    dispatch({type: actions.PRECONSENT_LOADER, data: {slug: slug}});

    try {       
        
        let { data, status } = await requestWithSession.post('/initialiseconsent', formData);

        switch (status) {            
            case 200:
                if(data.redirect_url){
                    dispatch(postMessageSuccess("redirect_to_dataholder", {data: slug}));
                    dispatch({ type: actions.CONSENT_INITIALISED, data: {
                        slug: slug, 
                        redirect_url:data.redirect_url,
                        consent_guid:data.consent_guid
                    }});
                } 
                else if (formData && formData.verification_method === 'mobile' && data.consent_guid) {
                    if(data.success === true){
                        // Mobile verification - start checking consent status
                        //dispatch(postMessageSuccess("option_selected_mobile", {data: slug}, "approved"));
                        dispatch({ type: actions.CONSENT_DECOUPLED_PENDING, data: {
                            slug: slug,
                            consent_guid:data.consent_guid
                        }});
                    } else {
                        // Handle mobile verification errors
                        const currentState = getState();
                        const storedDecoupledFlow = currentState.currentLogin?.decoupled_flow || currentState.sessionData?.decoupled_flow;
                        
                        let errorMessage = 'The mobile number you entered could not be verified. Please check the number and try again.';
                        
                        if (data.error === 'ciba_invalid_request' || data.error_message) {
                            if (data.error_message) {
                                errorMessage = data.error_message;
                            } else if (data.error === 'ciba_invalid_request') {
                                errorMessage = 'The mobile number you entered is not valid. Please check the number and try again.';
                            }
                        }
                        
                        // Return to decoupled initialised state with error
                        dispatch({ type: actions.CONSENT_DECOUPLED_INITIALISED, data: {
                            slug: slug, 
                            decoupled_flow: storedDecoupledFlow,
                            consent_guid: data.consent_guid,
                            user_token: currentState.currentLogin?.user_token || currentState.sessionData?.user_token,
                            error_message: errorMessage,
                            phone_error: true,
                            phone_number: formData.phone_number,
                            phone_failed_attempts: (currentState.currentLogin?.phone_failed_attempts || 0) + 1
                        }});
                        
                        dispatch(postMessageSuccess("option_selected_mobile", {data: slug}, "failed"));
                    }
                }
                else {
                    dispatch(postMessageSuccess("redirect_to_dataholder", {data: slug}, "fails"));
                    dispatch({ type: actions.CONSENT_ERROR, data: {slug: slug}})
                }
                break
            default:
                //dispatch({ type: actions.CONSENT_ERROR, data: {slug: slug}}) 
                dispatch(institutionApiError('CONSENT_INITIALISATION', {status: status, response: data}, slug));
        }
        
    } catch(error) {
        if (error.response) { //We got some answer from API
            dispatch(institutionApiError('CONSENT_INITIALISATION', error.response.data, slug));
        } else if (error.message) {
            dispatch(institutionApiError('CONSENT_INITIALISATION', error.message, slug));
        } else {
            dispatch(institutionApiError('CONSENT_INITIALISATION', error.toString(), slug));
        }
        dispatch(postMessageError("consent_initialised", 0, 'N/A'));
    }
}

// Function performs Decoupled Consent Submission with verification method
export const approveDecoupledFlow = (consent_guid, institution) => async (dispatch, getState) => {
    
    const slug = institution.slug;
    //dispatch({type: actions.PRECONSENT_LOADER, data: {slug: slug}});

    try {       
        
        let { data, status } = await requestWithSession.post('/approve_decoupled_flow', { consent_guid, slug });

        switch (status) {            
            case 200:
                dispatch(postMessageSuccess("decoupled_flow_approved", {slug: slug}));
                break;
            default:
                dispatch(institutionApiError('APPROVE_DECOUPLED_FLOW', {status: status, response: data}, slug));
        }
        
    } catch(error) {
        const slug = institution.slug;
        if (error.response) {
            dispatch(institutionApiError('APPROVE_DECOUPLED_FLOW', error.response.data, slug));
        } else if (error.message) {
            dispatch(institutionApiError('APPROVE_DECOUPLED_FLOW', error.message, slug));
        } else {
            dispatch(institutionApiError('APPROVE_DECOUPLED_FLOW', error.toString(), slug));
        }
        dispatch(postMessageError("approve_decoupled_flow", 0, 'N/A'));
    }
}

const CONSENT_STATUS_REQUEST_TIMEOUT = 15000; //Short per-request timeout so a hung portal call cannot stall polling for the full axios timeout
const CONSENT_STATUS_POLL_INTERVAL = 10000;
const CONSENT_STATUS_MIN_GAP = 2000; //Breathing room when a request took longer than the interval

export const checkConsentStatus = (consent_guid, institution, startTime = Date.now(), showLoader = true) => async (dispatch, getState) => {
    let slug = institution.slug
    if (showLoader) {
        dispatch({type: actions.PRECONSENT_LOADER, data: {slug: slug, title: "Awaiting Consent Authorisation from " + institution.name, 'showKeepWindowMsg': true}});
    }

    // Check if 15 minutes have passed (900,000 milliseconds)
    const currentTime = Date.now();
    const elapsedTime = currentTime - startTime;
    const timeoutDuration = 15 * 60 * 1000; // 15 minutes in milliseconds

    if (elapsedTime >= timeoutDuration) {
        dispatch({ type: actions.CONSENT_ERROR, data: {slug: slug} });
        dispatch(institutionApiError('CONSENT_ERROR', '(19002) Consent authorization timed out. Please try again.', slug));
        dispatch(postMessageError("consent_timeout", 0, 'Consent authorization timed out'));
        return;
    }

    const requestStartedAt = Date.now();

    //Interval is measured from the start of the request, so a slow response does not push the cadence out
    const scheduleNextPoll = () => {
        const requestDuration = Date.now() - requestStartedAt;
        const delay = Math.max(CONSENT_STATUS_POLL_INTERVAL - requestDuration, CONSENT_STATUS_MIN_GAP);
        setTimeout(() => {
            dispatch(checkConsentStatus(consent_guid, institution, startTime, showLoader));
        }, delay);
    }

    try {       
        let { data, status } = await requestWithSession.post('/checkConsentStatus', {}, {timeout: CONSENT_STATUS_REQUEST_TIMEOUT});

        if (status === 200) {
            if (data.scvOBConsentStatus === 'Authorised') {
                dispatch(postMessageSuccess("consent_authorised", {slug: slug}));
                dispatch({type: actions.PRECONSENT_LOADER, data: {slug: slug, title: "Please wait while we process your request", 'showKeepWindowMsg': true}});
                await requestWithSession.post('/nzob_export');
                dispatch({ type: actions.LOGIN_PROCESS_FINISHED, data: {slug: institution.slug, name: institution.name}}) 
                dispatch({ type: actions.CONSENT_SUCCESS, data: {slug: slug} });
                dispatch(postMessageSuccess("data_retrieved", {slug: slug}));
            } else if (data.scvOBConsentStatus !== 'AwaitingAuthorisation') {
                dispatch({ type: actions.CONSENT_ERROR, data: {slug: slug} });
                if(data.scvOBConsentStatus === 'Rejected') {
                    dispatch(postMessageSuccess("consent_rejected", {slug: slug}));
                } else if(data.scvOBConsentStatus === 'Revoked') {
                    dispatch(postMessageSuccess("consent_revoked", {slug: slug}));
                } else {
                    dispatch(postMessageSuccess("consent_error", {slug: slug}));                    
                }
                
            } else {
                // For other statuses, poll again after 10 seconds
                dispatch(postMessageSuccess("awaiting_consent", {slug: slug}, "AwaitingAuthorisation"));
                scheduleNextPoll();
            }
        } else {
            // Non-200 (e.g. 500 "Could not get consent status") - stay on the current screen
            // and keep polling. slug/redirect_url must be preserved or the institution lookup breaks.
            const redirect_url = getState().currentLogin.redirect_url;
            if (redirect_url) {
                dispatch({ type: actions.CONSENT_INITIALISED, data: {
                    slug: slug,
                    consent_guid: consent_guid,
                    redirect_url: redirect_url
                }});
            }
            scheduleNextPoll();
        }
        
    } catch(error) {
        //Timeouts / network blips must not kill the polling chain - retry until the 15 minute cutoff
        if (!error.response) {
            scheduleNextPoll();
            return;
        }

        dispatch(institutionApiError('CONSENT_TRANSFER', error.response.data, slug));
        dispatch(postMessageError("consent_error", 0, 'N/A'));
    }
}


export const downloadConsent = (consent_guid, slug) => async (dispatch) => {    

    try {       
        let { data, status } = await requestWithSession.post('/download_consent', { "consent_guid" : consent_guid });

        if (status === 200) {
            if (data.pdf) {
                
                // Convert base64 to binary
                const byteCharacters = atob(data.pdf);
                const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });

                // Create download link
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `consent_${consent_guid}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);

            } else {
                dispatch({ type: actions.CONSENT_ERROR, data: {slug: slug} });
            }
        } else {
            dispatch(institutionApiError('CONSENT_TRANSFER', data, slug));
        }
        
    } catch(error) {
        if (error.response) {
            dispatch(institutionApiError('CONSENT_TRANSFER', error.response.data, slug));
        } else if (error.message) {
            dispatch(institutionApiError('CONSENT_TRANSFER', error.message, slug));
        } else {
            dispatch(institutionApiError('CONSENT_TRANSFER', error.toString(), slug));
        }
        dispatch(postMessageError("consent_error", 0, 'N/A'));
    }
}

